// src/components/music/parts/TableRow.jsx

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, ShoppingCart, Plus, Download, CheckCircle } from 'lucide-react';
import { getTypeLabel } from '../utils/badgeUtils';

export const TableRow = ({
  track,
  index,
  isCurrent,
  isPlaying,
  playQueue,
  onPlayPause,
  onPurchase,
  processingPayment,
  onAddToPlaylist,
  currentUser,
  isPurchased,
  onDownload
}) => {
  const [downloadingId, setDownloadingId] = useState(null);
  const queueIndex = playQueue.findIndex(t => t.id === track.id);
  const purchased = isPurchased && typeof isPurchased === 'function' ? isPurchased(track.id) : false;

  const handleDownload = async (track) => {
    setDownloadingId(track.id);
    try {
      await onDownload(track);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <motion.tr
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.3, delay: (index % 10) * 0.05 }}
      viewport={{ once: true }}
      className={`border-b border-white/5 hover:bg-white/5 transition-all font-sans ${
        isCurrent ? 'bg-[#bd9f67]/10' : ''
      }`}
    >
      {/* Track Name with Play Button */}
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onPlayPause(track, queueIndex)}
            className={`p-2 rounded-full transition flex-shrink-0 shadow-sm ${
              isCurrent && isPlaying
                ? 'bg-[#bd9f67] text-gray-950 font-bold'
                : isCurrent
                ? 'bg-[#bd9f67]/30 text-[#bd9f67]'
                : 'bg-white/10 text-white/80 hover:bg-[#bd9f67] hover:text-gray-950'
            }`}
          >
            {isCurrent && isPlaying ? (
              <Pause size={14} fill="currentColor" />
            ) : (
              <Play size={14} fill="currentColor" className="ml-0.5" />
            )}
          </button>
          <div className="min-w-0">
            <p className={`font-semibold text-sm truncate ${isCurrent ? 'text-[#bd9f67]' : 'text-white/90'}`}>
              {track.name}
            </p>
            {purchased && (
              <div className="flex items-center gap-1 text-[#bd9f67] text-xs mt-0.5 font-medium">
                <CheckCircle size={12} />
                <span>Owned</span>
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Artist */}
      <td className="px-4 py-3.5 text-white/60 text-sm truncate">
        {track.artist || 'The Asidors'}
      </td>

      {/* Album (Hidden on Mobile) */}
      <td className="px-4 py-3.5 text-white/40 text-sm truncate hidden md:table-cell">
        {track.album || '-'}
      </td>

      {/* Type (Hidden on Mobile) */}
      <td className="px-4 py-3.5 hidden md:table-cell">
        <span className="inline-block bg-white/5 border border-white/10 text-white/80 text-xs font-semibold px-2.5 py-0.5 rounded-full">
          {getTypeLabel(track.type || 'song')}
        </span>
      </td>

      {/* Actions */}
      <td className="px-4 py-3.5">
        <div className="flex items-center justify-end gap-2">
          {purchased && currentUser ? (
            <>
              <button
                onClick={() => onAddToPlaylist(track)}
                className="flex items-center justify-center gap-1 py-1.5 px-2.5 rounded-lg text-xs font-semibold transition bg-white/5 border border-white/10 text-white/90 hover:bg-white/10 hover:text-white"
              >
                <Plus size={12} />
                <span className="hidden sm:inline">Add</span>
              </button>

              <button
                onClick={() => handleDownload(track)}
                disabled={downloadingId === track.id}
                className="flex items-center justify-center gap-1 py-1.5 px-2.5 rounded-lg text-xs font-semibold transition bg-[#bd9f67] text-gray-950 hover:bg-[#d0b07a] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <Download size={12} />
                <span className="hidden sm:inline">
                  {downloadingId === track.id ? 'Downloading...' : 'Download'}
                </span>
              </button>
            </>
          ) : !purchased && currentUser ? (
            <>
              <button
                onClick={() => onAddToPlaylist(track)}
                className="flex items-center justify-center gap-1 py-1.5 px-2.5 rounded-lg text-xs font-semibold transition bg-white/5 border border-white/10 text-white/90 hover:bg-white/10 hover:text-white"
              >
                <Plus size={12} />
                <span className="hidden sm:inline">Add</span>
              </button>

              <button
                onClick={() => onPurchase(track)}
                disabled={processingPayment === track.id}
                className="flex items-center justify-center gap-1 py-1.5 px-2.5 rounded-lg text-xs font-semibold transition bg-[#bd9f67] text-gray-950 hover:bg-[#d0b07a] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <ShoppingCart size={12} />
                <span className="hidden sm:inline">
                  {processingPayment === track.id ? 'Processing...' : 'Buy'}
                </span>
              </button>
            </>
          ) : (
            <button
              onClick={() => onPurchase(track)}
              disabled={processingPayment === track.id}
              className="flex items-center justify-center gap-1 py-1.5 px-2.5 rounded-lg text-xs font-semibold transition bg-[#bd9f67] text-gray-950 hover:bg-[#d0b07a] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              <ShoppingCart size={12} />
              <span className="hidden sm:inline">
                {processingPayment === track.id ? 'Processing...' : 'Buy'}
              </span>
            </button>
          )}
        </div>
      </td>
    </motion.tr>
  );
};
